'use client';

import { DotOutline, DoubleCheck } from '@/icons';
import { cn } from '@/lib/utils';
import { LoaderCircle } from 'lucide-react';
import React, { useState } from 'react';
import { StepItemContext, StepperContext } from './context';
import { useStepItem } from './hooks/useStepItem';
import { useStepper } from './hooks/useStepper';
import { StepState } from './types';

export function Stepper({
  defaultValue = 0,
  value,
  onValueChange,
  orientation = 'horizontal',
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  defaultValue?: number;
  value?: number;
  onValueChange?: (val: number) => void;
  orientation?: 'horizontal' | 'vertical';
}) {
  const [internalStep, setInternalStep] = useState(defaultValue);
  const currentStep = value ?? internalStep;

  const setActiveStep = (step: number) => {
    if (value === undefined) setInternalStep(step);
    onValueChange?.(step);
  };

  return (
    <StepperContext.Provider value={{ activeStep: currentStep, setActiveStep, orientation }}>
      <div
        className={cn(
          'group/stepper inline-flex',
          'data-[orientation=horizontal]:w-full data-[orientation=horizontal]:flex-row',
          'data-[orientation=vertical]:flex-col',
          className,
        )}
        data-orientation={orientation}
        {...props}
      />
    </StepperContext.Provider>
  );
}

export function StepperItem({
  step,
  completed = false,
  disabled = false,
  loading = false,
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  step: number;
  completed?: boolean;
  disabled?: boolean;
  loading?: boolean;
}) {
  const { activeStep } = useStepper();

  const state: StepState = completed || step < activeStep ? 'completed' : step === activeStep ? 'active' : 'inactive';

  const isLoading = loading && step === activeStep;

  return (
    <StepItemContext.Provider value={{ step, state, isDisabled: disabled, isLoading }}>
      <div
        className={cn(
          'group/step flex items-center ',
          'group-data-[orientation=horizontal]/stepper:flex-row',
          'group-data-[orientation=vertical]/stepper:flex-col',
          className,
        )}
        data-state={state}
        {...(isLoading ? { 'data-loading': true } : {})}
        {...props}
      >
        {children}
      </div>
    </StepItemContext.Provider>
  );
}

export function StepperTrigger({
  asChild = false,
  className,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean; className?: string }) {
  const { setActiveStep } = useStepper();
  const { step, isDisabled } = useStepItem();

  if (asChild) {
    return <div className={className}>{children}</div>;
  }

  return (
    <button
      type="button"
      className={cn('inline-flex items-center gap-2 disabled:pointer-events-none disabled:opacity-50', className)}
      onClick={() => setActiveStep(step)}
      disabled={isDisabled}
      {...props}
    >
      {children}
    </button>
  );
}

export function StepperIndicator({
  asChild = false,
  variant = 'icon',
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  asChild?: boolean;
  variant?: 'icon' | 'number';
}) {
  const { state, step, isLoading } = useStepItem();
  const Icon = state === 'completed' ? DoubleCheck : DotOutline;

  const indicator =
    variant === 'icon' ? (
      <Icon
        className="absolute fill-current"
        size={state === 'completed' ? 24 : 10}
        strokeWidth={2}
        aria-hidden="true"
      />
    ) : (
      <span>{step}</span>
    );

  return (
    <div
      className={cn(
        'relative border-2 flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-muted-foreground',
        'data-[state=completed]:border-success data-[state=completed]:bg-success/15 data-[state=completed]:text-success',
        'data-[state=active]:border-primary data-[state=active]:text-primary',
        className,
      )}
      data-state={state}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {indicator}
          {isLoading && (
            <span className="absolute transition-all">
              <LoaderCircle className="animate-spin" size={14} strokeWidth={2} aria-hidden="true" />
            </span>
          )}
        </>
      )}
    </div>
  );
}

export function StepperTitle({ className, children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={cn('font-bold', className)} {...props}>
      {children}
    </h3>
  );
}

export function StepperDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-secondary', className)} {...props} />;
}

export function StepperSeparator({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'm-0.5 bg-muted',
        'group-data-[orientation=horizontal]/stepper:h-0.5 group-data-[orientation=horizontal]/stepper:w-full ',
        'group-data-[orientation=vertical]/stepper:h-12 group-data-[orientation=vertical]/stepper:w-0.5',
        'group-data-[orientation=horizontal]/stepper:flex-1',
        'group-data-[state=completed]/step:bg-success',
        className,
      )}
      {...props}
    />
  );
}
